import { useState, useEffect, Suspense, lazy } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateUserInitialization } from "./CreateUser.hook";
import BaseComponent from "common/Components/BaseComponent";
import Loading from "common/Components/Loading";
import CustomAlert from "common/Components/CustomAlert";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import Select from "react-select";
import "./CreateUser.styles.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import parsePhoneNumber from "libphonenumber-js";
import { cleanMobilenumberString } from "common/Utilities/Utilities.functions";

const ErrorPage = lazy(() => import("common/Components/ErrorPage"));

// const mobileNumberPattern = /^\+\d{1,4}[\s.-]?\(?\d{1,4}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}$/;

const CreateUser = () => {
  const { state, actions } = useCreateUserInitialization();
  let roles = INFRAMARK_CONSTANTS.users.dropdown.roles;
  const defaultValues = {
    username: "",
    email: "",
    enabled: true,
    attributes: {
      customerCode: [""],
      role: "",
      mobilenumber: [""],
    },
  };
  const navigate = useNavigate();
  const [groupNames, setGroupNames] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isUserValidated, setIsUserValidated] = useState(false);
  const [isEmailValidated, setIsEmailValidated] = useState(false);
  const [isMobilenumberValidated, setIsMobilenumberValidated] = useState(false);
  const [phoneInputCountry, setPhoneInputCountry] = useState("us");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setError,
    clearErrors,
  } = useForm({
    defaultValues,
  });

  useEffect(() => {
    fetchGroupsListData();
  }, []);

  const onSubmit = (data) => {
    let formErrors = false;
    if (data.email !== data.confirmEmail) {
      setError("confirmEmail", {
        type: "manual",
        message: "Emails do not match",
      });
      formErrors = true;
    }
    if (formErrors) {
      return;
    }

    // `react-phone-input-2` handles formatting internally, so you just need the raw value
    // The value from PhoneInput is already in E.164 format (e.g., +12345678900)
    const rawMobileNumber = data.attributes.mobilenumber?.[0];

    delete data["confirmEmail"];
    delete data["attributes"]["mobilenumber"];

    const finalData = {
      ...data,
      username: (data.username ?? "").toLowerCase(),
      attributes: {
        ...data.attributes,
        mobile_number: [`+${rawMobileNumber}`],
      },
    };

    delete finalData.attributes.countryCode;

    actions.addNewUser(finalData, () => {
      setShowSuccess(true);
      // Reset the form to default values
      reset(defaultValues);
    });
  };

  const handlePaste = (e) => {
    e.preventDefault();
  };

  const handleCopy = (e) => {
    e.preventDefault();
  };

  const handleCut = (e) => {
    e.preventDefault();
  };

  /**Fetch GroupName If they are not avaialbel */
  const handleDropdownClick = () => {
    if (groupNames.length === 0) {
      fetchGroupsListData();
    }
  };

  const fetchGroupsListData = async () => {
    
    try {
      const response = await actions.getAllGroups();
      const groupsArray = [...response];
      setGroupNames(groupsArray);
    } catch (error) {
      setGroupNames([]);
    } 
  };

  const handleKeyDown = (event) => {
    if (event.key === " " || event.keyCode === 32) {
      event.preventDefault();
    }
  };

  const handleKeyPress = (event) => {
    const regex = /[!@#$%^&*()\-+=,.;:'"<>?/\\|`~]/;

    if (regex.test(event.key)) {
      event.preventDefault();
    }
  };

  const validateUserId = async (value) => {
    if (value?.length <= 0) {
      return;
    }
    try {
      // check if the Username already exists if true exists else not exists
      const response = await actions.validateUser(value);
      setIsUserValidated(true);
      if (response?.error) {
        setError("username", {
          type: "manual",
          message: "Failed to validate Username",
        });
      } else if (response?.data) {
        setError("username", {
          type: "manual",
          message: "Username already exists",
        });
      } else {
        clearErrors("username");
      }
    } catch (error) {
      setError("username", {
        type: "manual",
        message: "Failed to validate Username",
      });
    }
  };

  const checkEmailAvaialbility = async (value) => {
    if (value?.length <= 0) {
      return;
    }
    try {
      const response = await actions.validateEmail(value);
      setIsEmailValidated(true);
      if (response?.error) {
        setError("email", {
          type: "manual",
          message: "Failed to validate email",
        });
      } else if (response?.data) {
        setError("email", {
          type: "manual",
          message: "This email is already in use. Try another one.",
        });
      } else {
        clearErrors("email");
      }
    } catch (error) {
      setError("email", {
        type: "manual",
        message: "Failed to validate email",
      });
    }
  };
  const checkMobileNumberAvailability = async (value) => {
    if (value?.length <= 0) {
      return;
    }
    try {
      const response = await actions.validateMobilenumber(
        cleanMobilenumberString(value)
      );
      setIsMobilenumberValidated(true);
      if (response?.error) {
        setError("attributes.mobilenumber[0]", {
          type: "manual",
          message: "Failed to validate mobile number",
        });
      } else if (response?.data) {
        setError("attributes.mobilenumber[0]", {
          type: "manual",
          message: "This mobile number is already in use. Try another one.",
        });
      } else {
        clearErrors("attributes.mobilenumber[0]");
      }
    } catch (error) {
      setIsUserValidated(true);
      setError("attributes.mobilenumber[0]", {
        type: "manual",
        message: "Failed to validate mobilenumber",
      });
    }
  };
  
  const handleCancel = () => {
    navigate("/");
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <BaseComponent>
        <div style={{ padding: "0px 32px" }}>
          <div style={{ padding: "16px 8px" }}>
            <ol className="breadcrumb breadcrum-margin" style={{ margin: 0 }}>
              <li className="breadcrumb-item">
                <button
                  className="breadcrumb-item-custom breadcrumb-title-font-style btn-breadcrum"
                  onClick={handleCancel}
                  style={{ color: "#1A6BE8", textDecoration: "none" }}
                >
                  Home
                </button>
              </li>
              <li
                className="breadcrumb-item breadcrumb-title-font-style active"
                aria-current="page"
              >
                Create User
              </li>
            </ol>
          </div>

          {state.loading && <Loading />}
          {state.error && (
            <Suspense fallback={<div>Loading...</div>}>
              <ErrorPage
                errorValue={state.error}
                handleGoBack={() => {
                  actions.refreshError();
                }}
              />
            </Suspense>
          )}

          {showSuccess && (
            <CustomAlert
              show={showSuccess}
              title={"Alert"}
              onOkay={() => {
                setShowSuccess(false);
              }}
              message="User created successfully"
            />
          )}

          {!state.error && !state.loading && (
            <div className="add-cust-form">
              <form
                className="col-12 row"
                style={{ padding: "0px" }}
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="col-12 row">
                  <div className="col-12 row">
                    <div className="col-md-6" style={{ paddingRight: "24px" }}>
                      <div className="form-group row row-padding">
                        <label
                          className="label-create-user"
                          htmlFor="attributes.customerCode.0"
                        >
                          Customer
                        </label>
                        <div className="col-12">
                          <Controller
                            control={control}
                            name="attributes.customerCode.0"
                            rules={{ required: "Customer is required" }}
                            render={({ field: { onChange, value, ref } }) => (
                              <Select
                                id="attributes.customerCode.0"
                                aria-label="Customer"
                                className="mt-1 textbox-style-change"
                                inputRef={ref}
                                options={groupNames.map((type) => ({
                                  value: type.customerCode,
                                  label: type.name,
                                }))}
                                value={
                                  groupNames
                                    .map((type) => ({
                                      value:
                                        type.customerCode,
                                      label: type.name,
                                    }))
                                    .find((option) => option.value === value) ||
                                  null
                                }
                                onChange={(selectedOption) => {
                                  onChange(
                                    selectedOption ? selectedOption.value : null
                                  );
                                }}
                                styles={{
                                  control: (provided, state) => ({
                                    ...provided,
                                    borderColor: errors?.attributes
                                      ?.customerCode?.[0]
                                      ? "red"
                                      : provided.borderColor,
                                  }),
                                }}
                                onClick={handleDropdownClick}
                              />
                            )}
                          />
                          {errors?.attributes?.customerCode?.[0] && (
                            <span style={{ color: "red", fontSize: "14px" }}>
                              {errors.attributes.customerCode[0].message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group row row-padding">
                        <label className="label-create-user" htmlFor="username">
                          Username
                        </label>
                        <input
                          className="form-control textbox-style-change mt-1"
                          id="username"
                          style={{
                            borderColor: errors?.username ? "red" : "",
                          }}
                          autoComplete="off"
                          autoCorrect="off"
                          type="text"
                          {...register("username", {
                            required: "Username is required",
                            onBlur: (e) => validateUserId(e.target.value),
                            onChange: (e) => setIsUserValidated(false),
                          })}
                          maxLength={100}
                          onKeyDown={(event) => {
                            handleKeyDown(event);
                            handleKeyPress(event);
                          }}
                          placeholder="Enter Username"
                        />
                        {errors?.username && (
                          <span style={{ color: "red", fontSize: "14px" }}>
                            {errors?.username?.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 row">
                    <div className="col-md-6" style={{ paddingRight: "24px" }}>
                      <div className="form-group row row-padding">
                        <label className="label-create-user" htmlFor="email">
                          Email
                        </label>
                        <input
                          className="form-control textbox-style-change"
                          id="email"
                          style={{
                            borderColor: errors?.email ? "red" : "",
                            marginTop: "0.35rem",
                          }}
                          type="email"
                          {...register("email", {
                            required: "Email is required",
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                              message: "Enter a valid email address",
                            },
                            onBlur: (e) => {
                              checkEmailAvaialbility(e.target.value);
                            },
                            onChange: (e) => {
                              clearErrors("confirmEmail");
                              clearErrors("email");
                              setIsEmailValidated(false);
                            },
                          })}
                          autoComplete="off"
                          autoCorrect="off"
                          placeholder="Enter Email Address"
                          onPaste={handlePaste}
                          onCopy={handleCopy}
                          onCut={handleCut}
                        />
                        {errors?.email && (
                          <span style={{ color: "red", fontSize: "14px" }}>
                            {errors.email.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group row row-padding">
                        <label
                          className="label-create-user"
                          htmlFor="confirmEmail"
                        >
                          Confirm Email
                        </label>
                        <input
                          className={`form-control textbox-style-change-placeholder ${
                            errors?.confirmEmail ? "error-border" : ""
                          }`}
                          id="confirmEmail"
                          style={{
                            marginTop: "0.35rem",
                          }}
                          type="email"
                          {...register("confirmEmail", {
                            required: "Confirm Email is required",
                          })}
                          autoComplete="off"
                          autoCorrect="off"
                          placeholder="Confirm Email Address"
                          onPaste={handlePaste}
                          onCopy={handleCopy}
                          onCut={handleCut}
                        />
                        {errors?.confirmEmail && (
                          <span style={{ color: "red", fontSize: "14px" }}>
                            {errors.confirmEmail.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 row">
                    <div className="col-md-6" style={{ paddingRight: "24px" }}>
                      <div className="form-group row row-padding">
                        <label
                          className=" label-create-user"
                          style={{ paddingBottom: "9px" }}
                        >
                          Mobile Number
                        </label>
                         <div className="col-12" style={{ padding: "0px" }}>
                        <Controller
                          control={control}
                          name="attributes.mobilenumber.0"
                          rules={{
                            required: "Mobile number is required",
                            validate: {
                              isValid: (value) => {
                                try {
                                  const parsed = parsePhoneNumber("+" + value);
                                  return (
                                    (
                                      parsed?.isValid() &&
                                      parsed?.countryCallingCode !==
                                      undefined) ||
                                    "Enter a valid mobile number including country code."
                                  );
                                } catch (err) {
                                return "Enter a valid mobile number.";
                                }
                              },
                            },
                          }}
                          render={({ field: { onChange, value } }) => (
                            <PhoneInput
                              country={phoneInputCountry}
                              preferredCountries={["in", "us", "ca"]}
                              value={value}
                              onChange={(phone, countryData) => {
                                onChange(phone);
                                setPhoneInputCountry(countryData.countryCode);
                                setIsMobilenumberValidated(false);
                                clearErrors("attributes.mobilenumber[0]");
                              }}
                              onBlur={(e) => {
                                checkMobileNumberAvailability(e.target.value);
                              }}
                              inputProps={{
                                name: "mobilenumber",
                                id: "mobilenumber",
                                required: true,
                                autoFocus: false,
                                className:
                                  "form-control textbox-style-change-placeholder mt-2",
                                style: {
                                  borderColor: errors?.attributes?.mobilenumber
                                    ? "red"
                                    : "",
                                  padding: "6px 56px",
                                  width: "100%",
                                },
                              }}
                              disableDropdown={false}
                              enableSearch={false} // if we want search option we can enable true this in dropdown we will get
                              inputClass="custom-phone-input"
                            />
                          )}
                        />
                      </div>
                        {errors?.attributes?.mobilenumber && (
                          <span style={{ color: "red", fontSize: "14px" }}>
                            {errors?.attributes?.mobilenumber[0]?.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6" style={{ paddingRight: "24px" }}>
                      <div className="form-group row row-padding">
                        <label
                          className="label-create-user"
                          htmlFor="attributes-role"
                        >
                          Role
                        </label>
                        <Controller
                          control={control}
                          name="attributes.role"
                          rules={{ required: "Role is required" }}
                          render={({ field: { onChange, value, ref } }) => (
                            <Select
                              id="attributes-role"
                              aria-label="Role"
                              className="mt-1 textbox-style-change"
                              inputRef={ref}
                              options={roles}
                              value={
                                roles.find(
                                  (option) => option.value === value
                                ) || null
                              }
                              onChange={(selectedOption) => {
                                onChange(
                                  selectedOption ? selectedOption.value : ""
                                );
                              }}
                              styles={{
                                control: (provided, state) => ({
                                  ...provided,
                                  borderColor: errors?.attributes?.role
                                    ? "red"
                                    : provided.borderColor,
                                }),
                              }}
                            />
                          )}
                        />
                        {errors?.attributes?.role && (
                          <span style={{ color: "red", fontSize: "14px" }}>
                            {errors.attributes.role.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 mt-3 txt-right">
                    <button
                      type="button"
                      className="btn btn-light btn-cncl"
                      onClick={() => {
                        navigate("/");
                      }}
                      style={{
                        textTransform: "uppercase",
                        padding: "6px 16px",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        textTransform: "uppercase",
                        padding: "6px 16px",
                      }}
                      className={`btn ${
                        Object.keys(errors).length > 0 ||
                        !isUserValidated ||
                        !isEmailValidated ||
                        !isMobilenumberValidated
                          ? "disable-btn-create"
                          : "btn-add-row"
                      }`}
                      disabled={
                        Object.keys(errors).length > 0 ||
                        !isUserValidated ||
                        !isEmailValidated ||
                        !isMobilenumberValidated
                      }
                    >
                      Save
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </BaseComponent>
    </Suspense>
  );
};

export default CreateUser;
